"use client";

import { useState, useMemo } from "react";
import Link from "next/link";
import { BookOpen, Search, AlertTriangle, GraduationCap } from "lucide-react";
import { Badge } from "@/components/ui/badge";

interface DiseaseItem {
  slug: string;
  subspecialty: string;
  title: string;
  category?: string;
  emergency: boolean;
  examImportance?: string;
  difficulty?: string;
}

interface SubspecGroup {
  id: string;
  name: string;
  items: DiseaseItem[];
}

interface Props {
  totalCount: number;
  grouped: SubspecGroup[];
}

export function DiseasesClient({ totalCount, grouped }: Props) {
  const [filter, setFilter] = useState<string | null>(null);
  const [search, setSearch] = useState("");

  const filteredGroups = useMemo(() => {
    let groups = filter ? grouped.filter((g) => g.id === filter) : grouped;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      groups = groups
        .map((g) => ({
          ...g,
          items: g.items.filter(
            (d) =>
              d.title.toLowerCase().includes(q) ||
              (d.category && d.category.toLowerCase().includes(q))
          ),
        }))
        .filter((g) => g.items.length > 0);
    }
    return groups;
  }, [filter, search, grouped]);

  const filteredCount = filteredGroups.reduce((sum, g) => sum + g.items.length, 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-clinical-blue/15">
            <BookOpen className="h-6 w-6 text-clinical-blue" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">질환 목록</h1>
            <p className="text-sm text-muted-foreground">
              {filteredCount}개 질환{filter || search ? " (필터 적용 중)" : ` · ${grouped.length}개 분과`}
            </p>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <input
          type="text"
          placeholder="질환명 검색..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-9 pr-4 py-2.5 rounded-lg border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
        />
      </div>

      {/* Subspecialty filter chips */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setFilter(null)}
          className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
            !filter
              ? "bg-primary text-primary-foreground"
              : "hover:bg-accent"
          }`}
        >
          전체 ({totalCount})
        </button>
        {grouped.map((g) => (
          <button
            key={g.id}
            onClick={() => setFilter(filter === g.id ? null : g.id)}
            className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
              filter === g.id
                ? "bg-primary text-primary-foreground"
                : "hover:bg-accent"
            }`}
          >
            {g.name} ({g.items.length})
          </button>
        ))}
      </div>

      {/* Disease list */}
      {filteredGroups.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          검색 결과가 없습니다.
        </div>
      ) : (
        filteredGroups.map((g) => (
          <section key={g.id} className="space-y-3">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold">{g.name}</h2>
              <Badge variant="outline" className="text-xs">{g.items.length}개</Badge>
            </div>
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {g.items.map((d) => (
                <Link
                  key={d.slug}
                  href={`/${d.subspecialty}/${d.slug}`}
                  className="group flex items-center gap-3 p-3 rounded-lg border hover:border-primary/30 hover:bg-accent/50 transition-all"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="font-medium text-sm group-hover:text-primary transition-colors truncate">
                        {d.title}
                      </span>
                      {d.emergency && (
                        <AlertTriangle className="h-3 w-3 text-clinical-red shrink-0" />
                      )}
                      {d.examImportance === "high-yield" && (
                        <GraduationCap className="h-3 w-3 text-clinical-amber shrink-0" />
                      )}
                    </div>
                    {d.category && (
                      <p className="text-xs text-muted-foreground truncate mt-0.5">{d.category}</p>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          </section>
        ))
      )}
    </div>
  );
}
